import { useState } from "react";
import EcoleShell from "@/components/layout/EcoleShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Link2, UserPlus, GitMerge, XCircle } from "lucide-react";
import {
  useEcoleDemandes,
  useTraiterDemande,
  useListEcoleEleves,
} from "@/lib/backoffice-hooks";
import type { EcoleDemande, TraiterInput } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { formatDate } from "@/lib/format";

export default function EcoleDemandesPage() {
  // Historique consultable (arbitrage 2026-08-10) : les demandes traitées
  // disparaissaient de l'écran — seule la piste d'audit en gardait trace.
  const [tab, setTab] = useState<"PENDING" | "TRAITEES">("PENDING");
  const { data: demandes, isLoading } = useEcoleDemandes();
  const { data: toutes, isLoading: loadingTraitees } = useEcoleDemandes("ALL");
  const { data: eleves } = useListEcoleEleves();
  const traiter = useTraiterDemande();

  const traitees = (toutes ?? []).filter((d) => d.statut !== "PENDING");

  // Demande pour laquelle on saisit un motif de rejet.
  const [rejectTarget, setRejectTarget] = useState<EcoleDemande | null>(null);
  const [rejectMotif, setRejectMotif] = useState("");
  // Demande pour laquelle on choisit les élèves source/cible d'une fusion.
  const [mergeTarget, setMergeTarget] = useState<EcoleDemande | null>(null);
  const [mergeSourceEleveId, setMergeSourceEleveId] = useState("");
  const [mergeEleveId, setMergeEleveId] = useState("");

  function run(id: string, body: TraiterInput, onDone?: () => void) {
    traiter.mutate(
      { id, body },
      {
        onSuccess: () => {
          toast.success("Demande traitée.");
          onDone?.();
        },
        onError: (err: unknown) => toast.error(friendlyError(err)),
      },
    );
  }

  const rows = demandes ?? [];

  return (
    <EcoleShell>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-navy">Demandes de rattachement</h1>
        <p className="text-sm text-slate-600">{rows.length} en attente</p>
      </div>

      <div className="mb-4 flex gap-1 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setTab("PENDING")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "PENDING"
              ? "border-navy text-navy"
              : "border-transparent text-slate-500 hover:text-navy"
          }`}
        >
          En attente ({rows.length})
        </button>
        <button
          type="button"
          onClick={() => setTab("TRAITEES")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "TRAITEES"
              ? "border-navy text-navy"
              : "border-transparent text-slate-500 hover:text-navy"
          }`}
        >
          Traitées ({traitees.length})
        </button>
      </div>

      {tab === "TRAITEES" && (
        <Card className="border border-slate-200 shadow-none">
          {loadingTraitees && (
            <div className="p-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin inline text-navy" />
            </div>
          )}
          {!loadingTraitees && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5">Nom</th>
                    <th className="text-left">Prénoms</th>
                    <th className="text-left">Décision</th>
                    <th className="text-left">Motif de rejet</th>
                    <th className="text-left">Reçue le</th>
                    <th className="text-left">Traitée le</th>
                  </tr>
                </thead>
                <tbody>
                  {traitees.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-500">
                        Aucune demande traitée.
                      </td>
                    </tr>
                  )}
                  {traitees.map((d) => (
                    <tr key={d.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-medium uppercase">{d.nom}</td>
                      <td className="py-3">{d.prenoms}</td>
                      <td className="py-3">
                        {d.statut === "REJECTED" ? (
                          <span className="text-xs font-semibold text-red-600">Rejetée</span>
                        ) : (
                          <span className="text-xs font-semibold text-green-700">Acceptée</span>
                        )}
                      </td>
                      <td className="py-3 text-xs text-slate-600">{d.motifRejet ?? "·"}</td>
                      <td className="py-3 text-xs text-slate-500">{formatDate(d.creeLe)}</td>
                      <td className="py-3 text-xs text-slate-500">
                        {d.traiteLe ? formatDate(d.traiteLe) : "·"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "PENDING" && (
      <Card className="border border-slate-200 shadow-none">
        {isLoading && (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin inline text-navy" />
          </div>
        )}
        {!isLoading && (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Nom</th>
                <th className="text-left">Prénoms</th>
                <th className="text-left">Naissance</th>
                <th className="text-left">Type</th>
                <th className="text-left">Match suggéré</th>
                <th className="text-left">Reçue le</th>
                <th className="pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-500">
                    Aucune demande en attente.
                  </td>
                </tr>
              )}
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-3 font-medium uppercase">{d.nom}</td>
                  <td className="py-3">{d.prenoms}</td>
                  <td className="py-3 text-slate-600">{d.dateNaissance ?? "·"}</td>
                  <td className="py-3 text-xs text-slate-500">{d.type}</td>
                  <td className="py-3 text-xs">
                    {d.matchSuggereEleveId ? (
                      <span className="text-green-700">Oui</span>
                    ) : (
                      <span className="text-slate-400">Aucun</span>
                    )}
                  </td>
                  <td className="py-3 text-xs text-slate-500">{formatDate(d.creeLe)}</td>
                  <td className="pr-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex flex-wrap justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() =>
                          run(d.id, { action: "accept_link", targetEleveId: d.matchSuggereEleveId! })
                        }
                        disabled={!d.matchSuggereEleveId || traiter.isPending}
                        className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Link2 className="w-3 h-3" /> Lier
                      </button>
                      <button
                        type="button"
                        onClick={() => run(d.id, { action: "accept_new" })}
                        disabled={traiter.isPending}
                        className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40"
                      >
                        <UserPlus className="w-3 h-3" /> Créer nouvel élève
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMergeTarget(d);
                          setMergeSourceEleveId("");
                          setMergeEleveId("");
                        }}
                        disabled={traiter.isPending}
                        className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40"
                      >
                        <GitMerge className="w-3 h-3" /> Fusionner
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setRejectTarget(d);
                          setRejectMotif("");
                        }}
                        disabled={traiter.isPending}
                        className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-40"
                      >
                        <XCircle className="w-3 h-3" /> Rejeter
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
      )}

      {/* Rejet : saisie du motif */}
      {rejectTarget && (
        <Card className="mt-4 border border-red-200 shadow-none p-4">
          <h2 className="text-sm font-bold text-navy mb-2">
            Rejeter la demande de {rejectTarget.nom} {rejectTarget.prenoms}
          </h2>
          <div className="space-y-2">
            <Label htmlFor="reject-motif">Motif du rejet</Label>
            <Input
              id="reject-motif"
              value={rejectMotif}
              onChange={(e) => setRejectMotif(e.target.value)}
              placeholder="Ex. pièce manquante, identité incorrecte…"
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setRejectTarget(null)}>
                Annuler
              </Button>
              <Button
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={!rejectMotif.trim() || traiter.isPending}
                onClick={() =>
                  run(rejectTarget.id, { action: "reject", motif: rejectMotif.trim() }, () =>
                    setRejectTarget(null),
                  )
                }
              >
                Confirmer le rejet
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Fusion : choix de l'élève cible */}
      {mergeTarget && (
        <Card className="mt-4 border border-slate-200 shadow-none p-4">
          <h2 className="text-sm font-bold text-navy mb-2">
            Fusionner deux élèves doublons (demande de {mergeTarget.nom} {mergeTarget.prenoms})
          </h2>
          <p className="text-xs text-slate-500 mb-2">
            L'élève source (le doublon) est fusionné dans l'élève cible (celui à conserver).
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="merge-source">Élève source (doublon)</Label>
              <select
                id="merge-source"
                className="w-full border border-slate-300 rounded px-2 h-9 text-sm"
                value={mergeSourceEleveId}
                onChange={(e) => setMergeSourceEleveId(e.target.value)}
              >
                <option value="">Sélectionner un élève…</option>
                {(eleves ?? []).map((e) => (
                  <option key={e.id} value={e.id} disabled={e.id === mergeEleveId}>
                    {e.matriculeInterne} · {e.nom} {e.prenoms}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="merge-eleve">Élève cible (à conserver)</Label>
              <select
                id="merge-eleve"
                className="w-full border border-slate-300 rounded px-2 h-9 text-sm"
                value={mergeEleveId}
                onChange={(e) => setMergeEleveId(e.target.value)}
              >
                <option value="">Sélectionner un élève…</option>
                {(eleves ?? []).map((e) => (
                  <option key={e.id} value={e.id} disabled={e.id === mergeSourceEleveId}>
                    {e.matriculeInterne} · {e.nom} {e.prenoms}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => setMergeTarget(null)}>
              Annuler
            </Button>
            <Button
              className="bg-navy text-white hover:bg-navy/90"
              disabled={
                !mergeSourceEleveId ||
                !mergeEleveId ||
                mergeSourceEleveId === mergeEleveId ||
                traiter.isPending
              }
              onClick={() =>
                run(
                  mergeTarget.id,
                  { action: "merge", targetEleveId: mergeEleveId, sourceEleveId: mergeSourceEleveId },
                  () => setMergeTarget(null),
                )
              }
            >
              Confirmer la fusion
            </Button>
          </div>
        </Card>
      )}
    </EcoleShell>
  );
}
